/**
 * Drill content model + authored seed set. Four interaction archetypes cover all eight
 * drills (BUILD_PLAN F-02..F-10): select, tag, predict, fill. Content is isomorphic —
 * the same module grades in the browser (instant) and the Worker (authoritative), so
 * keys living in the client bundle is fine for low-stakes practice.
 *
 * The broad item bank comes from the content factory; this is the hand-authored seed.
 */

import type { ReasoningSkill } from '@usacow/shared';
import type { DrillKind } from './types.js';

interface Base {
  id: string;
  kind: DrillKind;
  topic: string;
  skill: ReasoningSkill;
  prompt: string;
  explanation: string;
}

export interface SelectContent extends Base {
  archetype: 'select';
  options: { id: string; label: string }[];
  correctId: string;
  acceptedIds?: string[];
  distractors: { id: string; misconception: string }[];
}

export interface TagContent extends Base {
  archetype: 'tag';
  palette: string[]; // available tags
  spans: { id: string; text: string; gold: string }[]; // each span's correct tag
}

export interface PredictContent extends Base {
  archetype: 'predict';
  inputLabel: string;
  expected: string; // canonical expected answer (trimmed/compared)
  misconceptionOnFail?: string;
  context?: string; // mono state line shown to the student
}

export interface FillContent extends Base {
  archetype: 'fill';
  before: string;
  after: string;
  expected: string;
  bugTags: string[];
}

export type DrillContent = SelectContent | TagContent | PredictContent | FillContent;

export const DRILLS: Record<DrillKind, DrillContent[]> = {
  pattern_discriminator: [
    {
      id: 'pd_window_1',
      kind: 'pattern_discriminator',
      archetype: 'select',
      topic: 'two_pointers',
      skill: 'modeling',
      prompt: 'Which needs a different method than a fixed sliding window?',
      options: [
        { id: 'a', label: 'Max sum subarray of length exactly K' },
        { id: 'b', label: 'Max sum subarray of length at most K' },
      ],
      correctId: 'b',
      distractors: [{ id: 'a', misconception: 'misses-interval-events' }],
      explanation:
        '“At most K” breaks the fixed-window invariant — you need a deque or prefix+monotonic structure, not one sliding sum.',
    },
  ],
  constraint_sniffer: [
    {
      id: 'cs_nq_1',
      kind: 'constraint_sniffer',
      archetype: 'select',
      topic: 'prefix_sums',
      skill: 'algorithm_choice',
      prompt: 'N ≤ 2·10⁵, Q ≤ 2·10⁵, 2 s. Which is the slowest complexity you can afford?',
      options: [
        { id: 'a', label: 'O(N·Q)' },
        { id: 'b', label: 'O((N+Q) log N)' },
        { id: 'c', label: 'O(N+Q)' },
      ],
      correctId: 'b',
      acceptedIds: ['c'],
      distractors: [{ id: 'a', misconception: 'ignores-large-Q' }],
      explanation: 'O(N·Q) ≈ 4·10¹⁰ is far over budget; a log factor over (N+Q) is fine.',
    },
  ],
  invariant_builder: [
    {
      id: 'inv_dijkstra_1',
      kind: 'invariant_builder',
      archetype: 'select',
      topic: 'shortest_paths',
      skill: 'proof',
      prompt: 'Pick the loop invariant that proves Dijkstra correct.',
      options: [
        { id: 'a', label: 'dist[u] is final once u is popped' },
        { id: 'b', label: 'the queue is always sorted by insertion time' },
        { id: 'c', label: 'every edge is relaxed exactly once' },
      ],
      correctId: 'a',
      distractors: [
        { id: 'b', misconception: 'wrong-priority-queue-order' },
        { id: 'c', misconception: 'invariant-too-weak' },
      ],
      explanation: 'Non-negative weights guarantee a popped node’s distance can’t improve later.',
    },
  ],
  bug_trainer: [
    {
      id: 'bug_binsearch_1',
      kind: 'bug_trainer',
      archetype: 'select',
      topic: 'binary_search',
      skill: 'implementation',
      prompt: 'a=[1,3,5], x=4 → expected 2, got ∞ (loops). Click the buggy line.',
      options: [
        { id: 'l1', label: 'while (lo < hi) {' },
        { id: 'l2', label: '  int mid=(lo+hi)/2;' },
        { id: 'l3', label: '  if (a[mid] < x) lo=mid;' },
        { id: 'l4', label: '  else hi=mid;' },
      ],
      correctId: 'l3',
      distractors: [{ id: 'l2', misconception: 'off-by-one-prefix-range' }],
      explanation: 'lo=mid never advances when the window is size 2 — use lo=mid+1.',
    },
  ],
  deconstructor: [
    {
      id: 'dec_graph_1',
      kind: 'deconstructor',
      archetype: 'tag',
      topic: 'shortest_paths',
      skill: 'parsing',
      prompt: 'Tag each span by its role.',
      palette: ['entity', 'constraint', 'objective', 'noise'],
      spans: [
        { id: 's1', text: 'N barns', gold: 'entity' },
        { id: 's2', text: 'M roads', gold: 'entity' },
        { id: 's3', text: 'shortest route', gold: 'objective' },
        { id: 's4', text: 'barns are painted red', gold: 'noise' },
        { id: 's5', text: 'halve one road once', gold: 'constraint' },
      ],
      explanation: '“length” is an edge weight (entity); the paint color is story-only noise.',
    },
  ],
  state_visualizer: [
    {
      id: 'sv_bfs_1',
      kind: 'state_visualizer',
      archetype: 'predict',
      topic: 'bfs_dfs',
      skill: 'execution',
      prompt: 'BFS, queue=[c], visited={a,b}. Which node is popped next?',
      context: 'queue: [c] · dist: a0 b1 c1 d∞ e∞',
      inputLabel: 'node',
      expected: 'c',
      misconceptionOnFail: 'wrong-priority-queue-order',
      explanation: 'BFS pops the front of the queue in FIFO order — c.',
    },
  ],
  counterexample: [
    {
      id: 'ce_greedy_coins_1',
      kind: 'counterexample',
      archetype: 'predict',
      topic: 'advanced_greedy',
      skill: 'proof',
      prompt:
        'Greedy “largest coin ≤ remaining” fails for coins {1,3,4}. Enter an amount it gets wrong.',
      inputLabel: 'amount',
      expected: '6',
      misconceptionOnFail: 'uses-greedy-without-monotonicity',
      explanation: 'For 6, greedy takes 4+1+1 (3 coins); optimal is 3+3 (2 coins).',
    },
  ],
  implementation_bridge: [
    {
      id: 'ib_prefix_1',
      kind: 'implementation_bridge',
      archetype: 'fill',
      topic: 'prefix_sums',
      skill: 'implementation',
      prompt: 'Complete the prefix-sum build.',
      before: 'for (int i = 1; i <= N; i++) prefix[i] = ',
      after: ' + a[i];',
      expected: 'prefix[i-1]',
      bugTags: ['off-by-one-prefix-range'],
      explanation: 'Each prefix extends the previous: prefix[i] = prefix[i-1] + a[i].',
    },
  ],
};

export function pickDrill(kind: DrillKind): DrillContent | null {
  return DRILLS[kind]?.[0] ?? null;
}
